import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import { useModal } from '@hooks/modal/useModal';
import { useMainSelector } from '@hooks/useMainSelector';
import type React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type CreateSubfolderModalProps = {
  onSubmit: (
    parentFolderId: string,
    name: string,
    description: string,
  ) => Promise<void>;
  existingSubfolders?: { name: string }[];
};

export const CreateSubfolderModal: React.FC<CreateSubfolderModalProps> = ({
  onSubmit,
  existingSubfolders = [],
}) => {
  const {
    dataSources: { selectedFolder },
  } = useMainSelector();
  const { t } = useTranslation();
  const { hideModal } = useModal();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
    },
  });
  const onFormSubmit = async (data: { name: string; description: string }) => {
    try {
      await onSubmit(
        selectedFolder?._id?.toString() || '',
        data.name.trim(),
        data.description.trim(),
      );
      hideModal();
    } catch (error) {
      console.error('Failed to create subfolder:', error);
    } finally {
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-4'>
          <div className='col-span-1'>
            <InputWithLabelT
              control={control}
              field='name'
              label={`${t('data-sources.modals.create-subfolder.name-label')}`}
              placeholder={t(
                'data-sources.modals.create-subfolder.name-placeholder',
              )}
              options={{
                required: t('data-sources.validation.subfolder-name-required'),
                minLength: {
                  value: 3,
                  message: t('data-sources.validation.subfolder-name-min-3'),
                },
                maxLength: {
                  value: 50,
                  message: t('data-sources.validation.subfolder-name-max-50'),
                },
                validate: {
                  trim: (value: string) =>
                    !!value.trim() ||
                    t('data-sources.validation.subfolder-name-required'),
                  unique: (value: string) =>
                    !existingSubfolders.some(
                      (sf) =>
                        sf.name.toLowerCase() === value.trim().toLowerCase(),
                    ) || t('data-sources.validation.subfolder-name-duplicate'),
                },
              }}
              error={errors.name?.message}
            />
          </div>
          <div className='col-span-1'>
            <InputWithLabelT
              control={control}
              field='description'
              label={t(
                'data-sources.modals.create-subfolder.description-label',
              )}
              placeholder={t(
                'data-sources.modals.create-subfolder.description-placeholder',
              )}
              options={{
                maxLength: {
                  value: 200,
                  message: t('data-sources.validation.description-max-200'),
                },
              }}
              error={errors.description?.message}
            />
          </div>
        </div>
      </div>

      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT type='submit' styling='primary' size='xs' rounding='md'>
          {t('general.buttons.create')}
        </ButtonT>
      </div>
    </form>
  );
};
